import { useCallback, useMemo, useState } from 'react';
import { useAuthStore, useChatStore, useCollectionStore, useToastStore } from '@/store';
import { chatsApi } from '@/lib/api';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';

/**
 * State + actions for the "Move this chat to <collection>?" suggestion.
 *
 * A suggestion can arrive two ways:
 *   1. Live via socket after inference (chat store `collectionSuggestions`)
 *   2. Persisted on the serialized conversation (`chat.collectionSuggestion`,
 *      only present while status is pending) — covers reloads and chats
 *      opened later.
 *
 * The suggestion is hidden the moment the chat lands in a collection, the
 * user opts out in settings, or the user responds either way.
 */
export default function useCollectionSuggestion(conversationId) {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);

  const suggestionsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.collectionSuggestionsEnabled !== false
  );
  const chat = useChatStore(
    useCallback(state => state.chats.find(c => c.id === conversationId), [conversationId])
  );
  const socketSuggestion = useChatStore(state => state.collectionSuggestions[conversationId]);
  const clearCollectionSuggestion = useChatStore(state => state.clearCollectionSuggestion);
  const updateLocalChatDetails = useChatStore(state => state.updateLocalChatDetails);
  const collections = useCollectionStore(state => state.collections);
  const addCollection = useCollectionStore(state => state.addCollection);
  const addConversationToCollectionLocally = useCollectionStore(state => state.addConversationToCollectionLocally);

  const [isResponding, setIsResponding] = useState(false);
  // Scoped to the conversation id so switching chats within the same mounted
  // view doesn't carry a "resolved" flag over to another chat's suggestion.
  const [resolvedConversationId, setResolvedConversationId] = useState(null);
  const isResolved = resolvedConversationId === conversationId;

  const suggestion = useMemo(() => {
    if (!suggestionsEnabled || isResolved || !conversationId) return null;
    if (chat?.inCollectionId) return null;

    const raw = socketSuggestion
      || (chat?.collectionSuggestion?.status === 'pending' ? chat.collectionSuggestion : null);
    if (!raw?.collectionId) return null;

    const collectionId = String(raw.collectionId);
    const collection = collections?.[collectionId];
    const title = collection?.title || raw.collectionTitle;
    // Without a resolvable title the prompt is meaningless — stay quiet.
    if (!title) return null;

    return { collectionId, collectionTitle: title };
  }, [suggestionsEnabled, isResolved, conversationId, chat, socketSuggestion, collections]);

  const resolveLocally = useCallback(() => {
    setResolvedConversationId(conversationId);
    clearCollectionSuggestion(conversationId);
    updateLocalChatDetails(conversationId, { collectionSuggestion: null });
  }, [conversationId, clearCollectionSuggestion, updateLocalChatDetails]);

  const accept = useCallback(async () => {
    if (!suggestion || isResponding) return;
    setIsResponding(true);
    try {
      const { collection } = await chatsApi.respondToCollectionSuggestion(conversationId, 'accept');
      trackEvent('collections', 'suggestion_accepted', suggestion.collectionId);
      // Sync local state: chat gains inCollectionId, target collection gains
      // the conversation, any other collection loses it.
      addConversationToCollectionLocally(suggestion.collectionId, conversationId);
      if (collection) {
        addCollection(collection);
      }
      resolveLocally();
      addToast(t('collections.suggestion.moved', { title: suggestion.collectionTitle }), 'success');
    } catch (error) {
      console.error('Error accepting collection suggestion:', error);
      // The suggestion may have gone stale server-side (collection deleted,
      // chat moved elsewhere) — hide it rather than leaving a dead prompt.
      resolveLocally();
      addToast(t('collections.suggestion.moveFailed'), 'error');
    } finally {
      setIsResponding(false);
    }
  }, [suggestion, isResponding, conversationId, addConversationToCollectionLocally, addCollection, resolveLocally, addToast, t]);

  const dismiss = useCallback(() => {
    if (!suggestion || isResponding) return;
    trackEvent('collections', 'suggestion_dismissed', suggestion.collectionId);
    // Hide instantly; the server call is fire-and-forget since a lost dismiss
    // only means the (already once-per-chat) suggestion could reappear once.
    resolveLocally();
    chatsApi.respondToCollectionSuggestion(conversationId, 'dismiss').catch(() => {});
  }, [suggestion, isResponding, conversationId, resolveLocally]);

  return {
    suggestion,
    hasSuggestion: !!suggestion,
    isResponding,
    accept,
    dismiss,
  };
}
